import React, { useCallback, useRef, type ReactNode } from "react";
import Select from "react-dropdown-select";
import styles from "./DropdownSelect.module.css";
import { tokens } from "../../design-system/tokens";

const SelectAny: any = Select;

interface DropdownSelectProps {
  className?: string;
  ChildclassName?: string;
  SiteLang?: string;
  name?: string;
  onChange: (value: any) => void;
  values?: any[];
  defaultValue?: any;
  options?: any[];
  loading?: boolean;
  placeholder?: string;
  FieldDescription?: string;
  Err?: string;
  style?: React.CSSProperties;
  color?: "outline" | "outlineSecondary" | string;
  size?: "small" | "medium" | "large";
  width?: string | number;
  maxWidth?: string | number;
  dropdownMaxHeight?: string | number;
  /** When true, react-dropdown-select allows multiple selections. */
  multi?: boolean;
  /** When multi is true, keep the dropdown open after each selection (default false for multi). */
  closeOnSelect?: boolean;
  /** When multi is true, cap how many items can be selected (optional). */
  maxSelections?: number;
  /** react-dropdown-select: custom row in the dropdown list */
  itemRenderer?: (args: {
    item: Record<string, unknown>;
    itemIndex: number;
    props: Record<string, unknown>;
    state: Record<string, unknown>;
    methods: Record<string, unknown>;
  }) => ReactNode;
  /** react-dropdown-select: custom selected chip (multi) */
  optionRenderer?: (args: {
    item: Record<string, unknown>;
    props: Record<string, unknown>;
    state: Record<string, unknown>;
    methods: Record<string, unknown>;
  }) => ReactNode;
  /** Disables opening / changing selection (passed to react-dropdown-select). */
  disabled?: boolean;
}

const DropdownSelect = (props: DropdownSelectProps) => {
  const ref = useRef<any>(null);
  const isMulti = Boolean(props.multi);
  const maxSelections =
    typeof props.maxSelections === "number" && props.maxSelections > 0
      ? props.maxSelections
      : undefined;
  const closeOnSelect =
    props.closeOnSelect !== undefined ? props.closeOnSelect : !isMulti;
  const isOutline = props.color === "outline";
  const isOutlineSecondary = props.color === "outlineSecondary";
  const hasOutlineBorder = isOutline || isOutlineSecondary;
  const size = props.size || "large";
  const typographyBySize = {
    small: tokens.typography.inputSmall,
    medium: tokens.typography.inputMedium,
    large: tokens.typography.inputLarge,
  } as const;
  const controlTypography = typographyBySize[size];
  const sizeConfig = {
    small: {
      height: "30px",
      minHeight: "30px",
      borderRadius: tokens.borderRadius.md,
      padding: "0px 6px 0px 6px",
      lineHeight: String(tokens.typography.inputSmall.lineHeight),
    },
    medium: {
      height: "36px",
      minHeight: "36px",
      borderRadius: tokens.borderRadius.lg,
      padding: "0px 8px 0px 8px",
      lineHeight: String(tokens.typography.inputMedium.lineHeight),
    },
    large: {
      height: "40px",
      minHeight: "40px",
      borderRadius: tokens.borderRadius.xl,
      padding: "0px 6px 0px 6px",
      lineHeight: String(tokens.typography.inputLarge.lineHeight),
    },
  } as const;
  const currentSize = sizeConfig[size];
  const borderColor = isOutlineSecondary
    ? tokens.buttonColors.outlineSecondary
    : tokens.buttonColors.outlinePrimary;
  const controlColor = tokens.colors.primary;
  const isDisabled = Boolean(props.disabled);

  const onChange = useCallback(
    (e: any[]) => {
      const list = Array.isArray(e) ? e : [];

      if (isMulti) {
        let next = list;
        if (maxSelections != null && list.length > maxSelections) {
          next = list.slice(0, maxSelections);
        }
        props.onChange(next);
        return;
      }
      if (list.length > 0) {
        props.onChange(list);
      }
    },
    [props, isMulti, maxSelections]
  );

  /** Block Enter from adding when multi + max reached (library bypasses parent onClick). */
  const handleKeyDownFn = useCallback(
    (args: {
      event: React.KeyboardEvent;
      state: { cursor: number | null; searchResults: unknown[]; values: unknown[] };
      methods: { isSelected: (item: unknown) => boolean };
    }) => {
      if (!isMulti || maxSelections == null) return false;
      const { event, state, methods } = args;
      if (event.key !== "Enter") return false;
      const cursor = state.cursor;
      const searchResults = state.searchResults;
      if (cursor == null || !Array.isArray(searchResults)) return false;
      const item = searchResults[cursor] as { disabled?: boolean } | undefined;
      if (!item || item.disabled) return false;
      if (methods.isSelected(item)) return false;
      if (state.values.length >= maxSelections) {
        event.preventDefault();
        return true;
      }
      return false;
    },
    [isMulti, maxSelections]
  );

  const clear = useCallback(() => {
    if (ref.current) {
      ref.current.clearAll();
    }
  }, []);

  let className = "FormDropdownSelect";
  if (props.SiteLang == "en") {
    className = "FormDropdownSelectEn";
  }

  const handleClosed = (
    <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 12.529 11.249">
      <path id="Path_91" data-name="Path 91" d="M7.563,10.5,12.326,2.25A1.5,1.5,0,0,0,11.028,0H1.5A1.5,1.5,0,0,0,.2,2.25L4.965,10.5a1.5,1.5,0,0,0,2.6,0" transform="translate(0)"
        fill={borderColor} />
    </svg>
  );

  const handleOpen = (
    <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 12.529 11.249">
      <path id="Path_91" data-name="Path 91" d="M7.563,10.5,12.326,2.25A1.5,1.5,0,0,0,11.028,0H1.5A1.5,1.5,0,0,0,.2,2.25L4.965,10.5a1.5,1.5,0,0,0,2.6,0" transform="translate(12.529 11.249) rotate(180)"
        fill={borderColor} />
    </svg>
  );

  let opacity = "1";
  if (ref.current?.state?.values == "") {
    opacity = "0";
  }

  let errOpacity = "0";
  if (props.Err !== "") {
    errOpacity = "1";
  }

  const fieldDescriptionStyle = { fontSize: "11px", color: "#979797", transition: ".2s", opacity };
  const errStyle = { fontSize: "11px", color: "red", transition: ".2s", opacity: errOpacity };

  return (
    <div
      className={`${props.className || ""} ${className}`}
      aria-disabled={isDisabled || undefined}
    >
      <div
        style={{ position: "absolute", opacity: "0", zIndex: "-5000" }}
        onClick={clear}
        id={`clear_${props.name}`}
      >
        a
      </div>

      <SelectAny
        disabled={isDisabled}
        values={props.values}
        defaultValue={props.defaultValue}
        options={props.options}
        onChange={onChange}
        handleKeyDownFn={maxSelections != null && isMulti ? handleKeyDownFn : undefined}
        loading={props.loading}
        itemRenderer={props.itemRenderer}
        optionRenderer={props.optionRenderer}
        className={styles.DropdownSelect}
        style={{
          border: hasOutlineBorder ? `1px solid ${borderColor}` : "none",
          borderBottom: hasOutlineBorder ? `1px solid ${borderColor}` : "1px solid #111230",
          borderRadius: currentSize.borderRadius,
          backgroundColor: hasOutlineBorder ? "transparent" : undefined,
          color: controlColor,
          fontFamily: controlTypography.fontFamily,
          fontWeight: controlTypography.fontWeight,
          fontSize: controlTypography.fontSize,
          lineHeight: currentSize.lineHeight,
          height: currentSize.height,
          minHeight: currentSize.minHeight,
          padding: currentSize.padding,
          width: props.width,
          maxWidth: props.maxWidth,
          cursor: isDisabled ? "not-allowed" : undefined,
          opacity: isDisabled ? 0.55 : undefined,
          transition: `background-color ${tokens.transitions.normal}, border-color ${tokens.transitions.normal}, color ${tokens.transitions.normal}`,
          ["--dropdown-scrollbar-color" as any]: tokens.colors.primary,
          ["--dropdown-control-color" as any]: controlColor,
          ["--dropdown-control-font-size" as any]: controlTypography.fontSize,
          ["--dropdown-control-font-weight" as any]: controlTypography.fontWeight,
          ["--dropdown-control-line-height" as any]: currentSize.lineHeight,
          ["--dropdown-max-height" as any]: props.dropdownMaxHeight,
          ...props.style,
        }}
        ref={ref}
        placeholder={props.placeholder}
        direction={"rtl"}
        multi={isMulti}
        closeOnSelect={closeOnSelect}
        dropdownHandleRenderer={({ state }: any) => (
          <>
            {state.dropdown ? (
              <div style={{ position: "relative", top: "1px", left: "2px" }}>{handleOpen}</div>
            ) : (
              <div style={{ position: "relative", top: "1px", left: "2px" }}>{handleClosed}</div>
            )}
          </>
        )}
      />
      <div style={{ display: "flex", width: "100%", justifyContent: "space-between" }}>
        <div style={fieldDescriptionStyle}>{props.FieldDescription}</div>
        <div style={errStyle}>{props.Err}</div>
      </div>
    </div>
  );
};

export default DropdownSelect;
